import { useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../api";
import {
  createResponseExport, responseExportFilename, ResponseExportLimitError,
  type ResponseDates, type ResponseExportQuiz,
} from "../utils/responseExport";
import {
  defaultResponseDates, streamResponsePages, responseDateBounds, throwIfExportAborted,
} from "../utils/responseExportFetch";

const INPUT_CLASS = "rounded-lg border border-mars-line bg-mars-night px-3 py-2 text-sm text-mars-white [color-scheme:dark] disabled:opacity-50";

function saveWorkbook(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  try {
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    // Give the browser time to begin reading the clicked URL before releasing it.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function failureMessage(error: unknown): string {
  if (error instanceof ResponseExportLimitError) return error.message;
  if (error instanceof ApiError && error.status === 404) {
    return "Response downloads are temporarily unavailable. Please try again shortly.";
  }
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return "Your admin session could not be verified. Refresh and sign in, then retry the download.";
  }
  const detail = error instanceof Error ? ` ${error.message}` : "";
  return `Download failed. Retry the download.${detail}`;
}

export function ResponsesExport({ quiz }: { quiz: ResponseExportQuiz }) {
  const id = useId();
  const [dates, setDates] = useState(defaultResponseDates);
  const [allTime, setAllTime] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const active = useRef<AbortController | null>(null);

  useEffect(() => {
    setDates(defaultResponseDates());
    setAllTime(false);
    setBusy(false);
    setStatus("");
    setError("");
    return () => {
      active.current?.abort();
      active.current = null;
    };
  }, [quiz.slug]);

  const resetMessage = () => { setStatus(""); setError(""); };
  const cancel = () => {
    active.current?.abort();
    active.current = null;
    setBusy(false);
    setStatus("Download cancelled. You can start again.");
  };

  async function download() {
    if (active.current) return;
    resetMessage();
    const selectedDates: ResponseDates = allTime ? {} : { ...dates };
    let bounds: ResponseDates;
    try {
      bounds = responseDateBounds(selectedDates);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Choose a valid date range.");
      return;
    }
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setStatus("Loading responses…");
    try {
      const workbook = createResponseExport({ quiz, ...selectedDates, generatedOn: new Date().toISOString() });
      const loaded = await streamResponsePages({
        quiz, bounds, signal: controller.signal,
        onPage: (rows, total) => workbook.append(rows, total),
        onProgress: (loaded, total) => {
          if (active.current === controller) setStatus(`Loaded ${loaded.toLocaleString()} of ${total.toLocaleString()} responses…`);
        },
      });
      throwIfExportAborted(controller.signal);
      if (loaded === 0) {
        setStatus(allTime ? "No saved responses found for this quiz." : "No responses found for this date range. Try a wider range or All time.");
        return;
      }
      setStatus(`Preparing ${loaded.toLocaleString()} ${loaded === 1 ? "response" : "responses"} for Excel…`);
      // Allow the progress update and any queued cancellation before creating the file.
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
      throwIfExportAborted(controller.signal);
      const blob = await workbook.finish(controller.signal);
      throwIfExportAborted(controller.signal);
      saveWorkbook(responseExportFilename(quiz.slug, selectedDates), blob);
      setStatus(`Downloaded ${loaded.toLocaleString()} ${loaded === 1 ? "response" : "responses"}.`);
    } catch (cause) {
      if (active.current === controller && !controller.signal.aborted) {
        setStatus("");
        setError(failureMessage(cause));
      }
    } finally {
      if (active.current === controller) {
        active.current = null;
        setBusy(false);
      }
    }
  }

  return (
    <section aria-labelledby={`${id}-heading`} className="mb-8 rounded-2xl border border-mars-line bg-mars-charcoal/40 p-6">
      <h2 id={`${id}-heading`} className="eyebrow">Saved responses</h2>
      <p className="mt-2 text-sm text-mars-white/70">One Excel row per saved response, with email, answers, response date, and tracked purchase status.</p>
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1.5 text-xs text-mars-white/60">
          From date
          <input type="date" value={dates.from} disabled={busy || allTime} className={INPUT_CLASS} onChange={(event) => { setDates((current) => ({ ...current, from: event.target.value })); resetMessage(); }} />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-mars-white/60">
          To date
          <input type="date" value={dates.to} disabled={busy || allTime} className={INPUT_CLASS} onChange={(event) => { setDates((current) => ({ ...current, to: event.target.value })); resetMessage(); }} />
        </label>
        <label className="flex min-h-10 items-center gap-2 text-sm text-mars-white/80">
          <input type="checkbox" checked={allTime} disabled={busy} className="accent-mars-orange" onChange={(event) => { setAllTime(event.target.checked); resetMessage(); }} />
          All time
        </label>
        <button type="button" onClick={() => void download()} disabled={busy} className="mono rounded-xl border border-mars-orange px-5 py-2.5 text-[0.7rem] font-bold uppercase tracking-[0.12em] text-mars-orange transition-colors hover:bg-mars-orange/10 disabled:cursor-wait disabled:opacity-50">
          Download responses
        </button>
        {busy ? <button type="button" onClick={cancel} className="rounded-lg px-3 py-2 text-sm text-mars-white/70 underline hover:text-mars-white">Cancel download</button> : null}
      </div>
      <p className="mt-3 text-xs text-mars-white/50">{allTime ? "All saved response dates (UTC)." : "Response dates in UTC; both selected days included. Last 30 days by default."}</p>
      {status ? <p role="status" aria-live="polite" className="mt-3 text-sm text-mars-white/80">{status}</p> : null}
      {error ? <p role="alert" className="mt-3 text-sm text-mars-orange">{error}</p> : null}
    </section>
  );
}
